import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PosterPackButton } from "../components/PosterPackButton";
import { api } from "../lib/api";
import { PUBLIC_HOST } from "../lib/domain";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QR Cours d'essai — affiche un QR code pointant vers la page d'inscription
 * au cours d'essai (19,98 $) (/cours-essai). À mettre en avant au comptoir,
 * en vitrine, sur une affiche : les gens scannent et s'inscrivent eux-mêmes.
 * Le QR utilise le domaine courant (kaizenfranchise.com en production).
 */
const SOURCES = [
  { v: "qr-affiche", l: "Affiche au dojo" },
  { v: "qr-comptoir", l: "Comptoir / accueil" },
  { v: "qr-vitrine", l: "Vitrine / porte d'entrée" },
  { v: "qr-flyer", l: "Flyer / dépliant" },
  { v: "qr-carte", l: "Carte d'affaires" },
  { v: "qr-evenement", l: "Événement / kiosque" },
  { v: "qr-sac-cadeau", l: "🎁 Sac-cadeau (atelier)" },
];

// Presets de texte (issus du document marketing de Kru) — 100% dojo
const PRESETS = [
  {
    id: "familiale",
    label: "Version A — Familiale (recommandée porte)",
    title: "SCANNE ICI POUR COMMENCER TON PARCOURS",
    bullets: [
      "Réserve ton cours d'essai (19,98 $)",
      "Enfants, ados et adultes — tous niveaux",
      "Découvre nos horaires et inscris-toi en ligne",
    ],
  },
  {
    id: "pro",
    label: "Version B — Directe & professionnelle",
    title: "SCANNE ICI. RÉSERVE TON COURS D'ESSAI.",
    bullets: [
      "Cours d'essai 19,98 $ · Forfait Découverte 2 sem. 120 $ · sans engagement",
      "Accès direct à la réservation en ligne",
      "Choisis ton cours et ton horaire",
    ],
  },
];

// Barre de progression animée (affichée pendant la génération asynchrone).
const GenProgress = ({ value, colorClass, testid }) => (
  <div className="border border-zinc-800 rounded-xl p-8 flex flex-col items-center gap-4" data-testid={testid}>
    <div className="text-zinc-400 text-sm">L'IA dessine ton affiche… ({Math.round(value)} %)</div>
    <div className="w-full max-w-md h-2.5 bg-zinc-800 rounded-full overflow-hidden">
      <div className={`h-full ${colorClass} rounded-full transition-all duration-500 ease-out`} style={{ width: `${value}%` }} />
    </div>
    <div className="text-[11px] text-zinc-600">Génération en arrière-plan · cela prend environ 30 à 90 secondes.</div>
  </div>
);

export default function QREssaiModule() {
  const [source, setSource] = useState("qr-affiche");
  const [presetId, setPresetId] = useState("familiale");
  const [title, setTitle] = useState(PRESETS[0].title);
  const [bullets, setBullets] = useState(PRESETS[0].bullets.join("\n"));

  // Affiche IA (porte du dojo)
  const [posterLoading, setPosterLoading] = useState(false);
  const [posterProgress, setPosterProgress] = useState(0);
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterFilename, setPosterFilename] = useState(null);
  const [posterFormat, setPosterFormat] = useState("porte");

  // Affiche QR · Ville & entreprises (2 styles, 4 formats, texte éditable)
  const [qvStyle, setQvStyle] = useState("epuree");
  const [qvFormat, setQvFormat] = useState("porte");
  const [qvTitle, setQvTitle] = useState("Inscription cours d'essai en 1 scan");
  const [qvBullets, setQvBullets] = useState("Cours d'essai 19,98 $ · Forfait Découverte 2 sem. 120 $ · sans engagement\nAccès direct à la réservation en ligne\nChoisis ton cours et ton horaire");
  const [qvCta, setQvCta] = useState("COURS D'ESSAI 19,98 $");
  const [qvLoading, setQvLoading] = useState(false);
  const [qvProgress, setQvProgress] = useState(0);
  const [qvUrl, setQvUrl] = useState(null);
  const [qvPreview, setQvPreview] = useState(false);
  const [qvScans, setQvScans] = useState(null);
  const [qvEmplacement, setQvEmplacement] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishingLi, setPublishingLi] = useState(false);
  const [caption, setCaption] = useState("");

  // Affiche Prestige & disciplines (2ᵉ porte du dojo)
  const [prestigeLoading, setPrestigeLoading] = useState(false);
  const [prestigeProgress, setPrestigeProgress] = useState(0);
  const [prestigeUrl, setPrestigeUrl] = useState(null);
  const [prestigeFormat, setPrestigeFormat] = useState("porte");
  const [prestigeStyle, setPrestigeStyle] = useState("epuree");
  const [prestigeEmpl, setPrestigeEmpl] = useState("");

  // Suivi UNIFIÉ (QR Ville · Prestige · PowerKick Her), trié par meilleur emplacement
  const [unified, setUnified] = useState(null);
  const [unifiedBusy, setUnifiedBusy] = useState(false);
  const loadUnified = async () => {
    setUnifiedBusy(true);
    try {
      const { data } = await api.get("/poster/tracking/unified");
      setUnified(data);
    } catch { /* noop */ } finally { setUnifiedBusy(false); }
  };

  // Sonde une tâche de génération d'affiche (arrière-plan) jusqu'à son achèvement.
  // Évite l'erreur Cloudflare 520 : l'appel IA ne bloque plus la requête HTTP.
  const pollPosterJob = async (jobId) => {
    const deadline = Date.now() + 240000; // 4 min max
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/poster/job/${jobId}`);
      if (data.status === "done") return data.result || {};
      if (data.status === "error") throw new Error(data.error || "Échec de la génération.");
    }
    throw new Error("Délai dépassé — réessaie.");
  };

  // Enrobe le sondage avec une progression animée (0 → 95 % basée sur le temps,
  // puis 100 % à l'achèvement). `setProgress` met à jour la barre de la section.
  const runPosterJob = async (jobId, setProgress) => {
    const start = Date.now();
    const expected = 70000; // durée estimée ~70 s
    const timer = setInterval(() => {
      const pct = Math.min(95, Math.round(((Date.now() - start) / expected) * 100));
      setProgress(pct);
    }, 500);
    try {
      const result = await pollPosterJob(jobId);
      setProgress(100);
      return result;
    } finally {
      clearInterval(timer);
    }
  };

  const generatePrestige = async () => {
    setPrestigeLoading(true);
    setPrestigeUrl(null);
    setPrestigeProgress(0);
    toast.info(prestigeStyle === "ia" ? "🎨 Génération de l'affiche Prestige IA…" : "🖨️ Création de l'affiche Prestige…");
    try {
      const { data } = await api.post("/poster/prestige", { format: prestigeFormat, style: prestigeStyle, emplacement: prestigeEmpl });
      const result = await runPosterJob(data.job_id, setPrestigeProgress);
      setPrestigeUrl(`${BACKEND_URL}${result.url}`);
      toast.success("✅ Affiche Prestige générée !");
      loadUnified();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Échec de la génération. Réessaie.");
    } finally {
      setPrestigeLoading(false);
    }
  };

  const downloadPrestige = async () => {
    if (!prestigeUrl) return;
    try {
      const res = await fetch(prestigeUrl);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "affiche-prestige-disciplines.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("📥 Affiche Prestige téléchargée");
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  // Affiche PowerKick Her (QR → page d'inscription /powerkick-her)
  const [pkhLoading, setPkhLoading] = useState(false);
  const [pkhProgress, setPkhProgress] = useState(0);
  const [pkhUrl, setPkhUrl] = useState(null);
  const [pkhFormat, setPkhFormat] = useState("porte");
  const [pkhCode, setPkhCode] = useState(null);
  const [pkhCampaigns, setPkhCampaigns] = useState(null);
  const [pkhStyle, setPkhStyle] = useState("ia");
  const [pkhEmpl, setPkhEmpl] = useState("");

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/powerkick-her/campaigns");
      setPkhCampaigns(data);
    } catch { /* silencieux */ }
  };

  const generatePowerkick = async () => {
    setPkhLoading(true);
    setPkhUrl(null);
    setPkhCode(null);
    setPkhProgress(0);
    toast.info(pkhStyle === "ia" ? "🎨 Génération de l'affiche PowerKick Her IA…" : "🖨️ Création de l'affiche PowerKick Her…");
    try {
      const { data } = await api.post("/poster/powerkick-her", { format: pkhFormat, promo: true, style: pkhStyle, emplacement: pkhEmpl });
      const result = await runPosterJob(data.job_id, setPkhProgress);
      setPkhUrl(`${BACKEND_URL}${result.url}`);
      setPkhCode(result.promo_code || null);
      toast.success("✅ Affiche PowerKick Her générée !");
      loadCampaigns();
      loadUnified();
    } catch (e) {
      toast.error(e?.message || "Échec de la génération — réessaie");
    } finally {
      setPkhLoading(false);
    }
  };

  const downloadPowerkick = async () => {
    if (!pkhUrl) return;
    try {
      const res = await fetch(pkhUrl);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "affiche-powerkick-her.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("📥 Affiche PowerKick Her téléchargée");
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const applyPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPresetId(id);
    setTitle(p.title);
    setBullets(p.bullets.join("\n"));
  };

  const bulletList = bullets.split("\n").map((b) => b.trim()).filter(Boolean);

  const origin = window.location.origin;
  const url = `${origin}/cours-essai?src=${encodeURIComponent(source)}`;

  const generatePoster = async () => {
    setPosterLoading(true);
    setPosterUrl(null);
    setPosterProgress(0);
    toast.info("🎨 Génération de l'affiche en cours…");
    try {
      const { data } = await api.post("/poster/dojo-door", {
        qr_url: url,
        website: `${PUBLIC_HOST}`,
        phone: "819-527-9446",
        city: "Val-d'Or, QC",
        embed_qr: true,
        format: posterFormat,
      });
      const result = await runPosterJob(data.job_id, setPosterProgress);
      setPosterUrl(`${BACKEND_URL}${result.url}`);
      setPosterFilename(result.filename);
      toast.success("✅ Affiche générée !");
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Échec de la génération. Réessaie.");
    } finally {
      setPosterLoading(false);
    }
  };

  const generateQrVille = async () => {
    setQvLoading(true);
    setQvUrl(null);
    setQvProgress(0);
    toast.info(qvStyle === "ia" ? "🎨 Génération de l'affiche IA…" : "🖨️ Création de l'affiche…");
    try {
      const { data } = await api.post("/poster/qr-affiche", {
        style: qvStyle, format: qvFormat, title: qvTitle,
        bullets: qvBullets.split("\n").map((s) => s.trim()).filter(Boolean),
        cta: qvCta, qr_url: url, emplacement: qvEmplacement,
      });
      const result = await runPosterJob(data.job_id, setQvProgress);
      setQvUrl(`${BACKEND_URL}${result.url}`);
      toast.success("✅ Affiche QR générée !");
      loadQvScans();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Échec de la génération. Réessaie.");
    } finally {
      setQvLoading(false);
    }
  };

  const downloadQrVille = async () => {
    if (!qvUrl) return;
    try {
      const res = await fetch(qvUrl);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "affiche-qr-ville.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("📥 Affiche téléchargée");
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const loadQvScans = async () => {
    try {
      const { data } = await api.get("/poster/qr-ville/campaigns");
      setQvScans(data);
    } catch { /* noop */ }
  };

  const publishPoster = async () => {
    if (!posterFilename) return;
    setPublishing(true);
    toast.info("📣 Publication sur Facebook & Instagram…");
    try {
      const { data } = await api.post(
        "/poster/publish",
        { filename: posterFilename, caption, targets: ["facebook", "instagram"] },
        { timeout: 120000 }
      );
      const r = data.results || {};
      const fbOk = r.facebook?.ok;
      const igOk = r.instagram?.ok;
      if (fbOk && igOk) toast.success("✅ Publié sur Facebook et Instagram !");
      else if (fbOk) toast.success("✅ Publié sur Facebook. Instagram : " + (r.instagram?.error || "échec"));
      else if (igOk) toast.success("✅ Publié sur Instagram. Facebook : " + (r.facebook?.error || "échec"));
      else toast.error("Publication échouée — " + (r.facebook?.error || r.instagram?.error || "réseaux non connectés"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible");
    } finally {
      setPublishing(false);
    }
  };

  const publishLinkedIn = async () => {
    if (!posterUrl) return;
    setPublishingLi(true);
    toast.info("📣 Publication sur LinkedIn…");
    try {
      const { data } = await api.post(
        "/integrations/publish",
        { provider: "linkedin", platform: "linkedin", content: caption || "Institut Impact Élite Martial — Val-d'Or", media_url: posterUrl },
        { timeout: 120000 }
      );
      if (data.status === "published") toast.success("✅ Publié sur LinkedIn !");
      else toast.message("Publication LinkedIn enregistrée.");
    } catch (e) {
      const d = e?.response?.data?.detail || "Publication LinkedIn impossible";
      toast.error(d.includes("manquant") || d.includes("connecté") ? "Connecte d'abord LinkedIn dans Réglages → Intégrations." : d);
    } finally {
      setPublishingLi(false);
    }
  };

  const downloadPoster = async () => {
    if (!posterUrl) return;
    try {
      const res = await fetch(posterUrl);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "affiche-dojo-kaizen.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("📥 Affiche téléchargée");
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(url);
    toast.success("📋 Lien copié");
  };

  const downloadQR = () => {
    const svg = document.getElementById("qr-essai-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1024;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 1024, 1024);
      ctx.drawImage(img, 0, 0, 1024, 1024);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `qr-cours-essai-${source}.png`;
      a.click();
      toast.success("📥 QR téléchargé");
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="space-y-6" data-testid="qr-essai-module">
      {/* Header */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black no-print">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">🎟️ QR CODE · COURS D'ESSAI 19,98 $</div>
            <h1 className="text-3xl font-bold tracking-tight">Inscription cours d'essai en 1 scan</h1>
            <p className="text-zinc-500 text-sm mt-2">
              Affiche ce QR au dojo, au comptoir ou en vitrine. Les visiteurs le scannent et s'inscrivent
              eux-mêmes à un cours d'essai (19,98 $) — la demande arrive directement dans ton CRM.
            </p>
          </div>
          <Button onClick={() => window.print()} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold whitespace-nowrap" data-testid="print-essai-btn">
            🖨️ Imprimer l'affiche
          </Button>
        </div>
      </div>

      {/* SUIVI UNIFIÉ — toutes les affiches traçables, meilleurs emplacements d'abord */}
      <div className="border border-emerald-500/40 bg-gradient-to-br from-zinc-950 to-black rounded-2xl p-6 no-print" data-testid="unified-tracking-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex-1 min-w-0">
            <div className="text-emerald-400 text-[10px] tracking-[0.4em] mb-2">📊 SUIVI UNIFIÉ · TOUTES LES AFFICHES</div>
            <h2 className="text-lg font-bold">Meilleurs emplacements (scans → inscriptions)</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
              Toutes tes affiches traçables (QR Ville · Prestige · PowerKick Her) réunies dans un seul tableau,
              <strong> triées du meilleur au pire emplacement</strong> selon les inscriptions générées. Sais en un coup d'œil où coller le plus d'affiches.
            </p>
          </div>
          <Button onClick={loadUnified} disabled={unifiedBusy} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold whitespace-nowrap" data-testid="load-unified-btn">
            {unifiedBusy ? "⏳ Chargement…" : "🔄 Charger le suivi"}
          </Button>
        </div>
        {!unified ? (
          <p className="text-xs text-zinc-600">Clique « Charger le suivi » pour voir tes emplacements classés.</p>
        ) : unified.rows.length === 0 ? (
          <p className="text-xs text-zinc-600">Aucune affiche traçable encore. Génère une affiche QR Ville, Prestige ou PowerKick Her pour démarrer le suivi.</p>
        ) : (
          <div className="space-y-3">
            {unified.best_location && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm" data-testid="best-location-card">
                🏆 <strong className="text-emerald-300">Meilleur emplacement :</strong> {unified.best_location.emplacement}
                <span className="text-zinc-400"> — {unified.best_location.signups} inscription(s) · {unified.best_location.scans} scan(s) · {unified.best_location.conversion_pct}% · {unified.best_location.type}</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-rose-300" data-testid="unified-kpi-scans">{unified.totals.scans}</div><div className="text-[10px] text-zinc-500">Scans</div></div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-emerald-400" data-testid="unified-kpi-signups">{unified.totals.signups}</div><div className="text-[10px] text-zinc-500">Inscriptions</div></div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-amber-300">{unified.totals.conversion_pct}%</div><div className="text-[10px] text-zinc-500">Conversion</div></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-2">#</th><th className="py-1 pr-2">📍 Emplacement</th><th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Code</th><th className="py-1 pr-2">Format</th><th className="py-1 pr-2 text-center">Scans</th><th className="py-1 pr-2 text-center">Inscr.</th><th className="py-1 text-center">Conv.</th></tr></thead>
                <tbody>
                  {unified.rows.map((c, i) => (
                    <tr key={c.code} className="border-t border-zinc-900" data-testid={`unified-row-${c.code}`}>
                      <td className="py-1 pr-2 text-zinc-500">{i + 1}</td>
                      <td className="py-1 pr-2 text-zinc-200 font-medium">{c.emplacement || "—"}</td>
                      <td className="py-1 pr-2 text-zinc-400">{c.type}</td>
                      <td className="py-1 pr-2 font-mono text-rose-300">{c.code}</td>
                      <td className="py-1 pr-2 text-zinc-400">{c.format || "—"}</td>
                      <td className="py-1 pr-2 text-center text-zinc-300">{c.scans}</td>
                      <td className="py-1 pr-2 text-center text-emerald-400">{c.signups}</td>
                      <td className="py-1 text-center text-amber-300">{c.conversion_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-zinc-600">💡 Renseigne le champ « 📍 Emplacement » lors de la génération de chaque affiche pour comparer les commerces/vitrines qui convertissent le mieux.</p>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* QR preview */}
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-2xl p-6 flex flex-col items-center text-center no-print" data-testid="qr-preview">
          <div className="bg-white p-4 rounded-xl">
            <QRCodeSVG id="qr-essai-svg" value={url} size={240} bgColor="#FFFFFF" fgColor="#000000" level="M" />
          </div>
          <div className="text-base font-extrabold mt-4 uppercase tracking-wide">{title}</div>
          <ul className="text-sm text-zinc-300 mt-3 space-y-1 text-left inline-block">
            {bulletList.map((b, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-amber-400">✓</span> {b}</li>
            ))}
          </ul>
          <div className="mt-3 text-[10px] text-zinc-500 font-mono break-all" title={url}>{url}</div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <button onClick={copyUrl} className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded-full px-4 py-1.5" data-testid="copy-essai-url">📋 Copier le lien</button>
            <button onClick={downloadQR} className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-full px-4 py-1.5" data-testid="download-essai-qr">📥 Télécharger QR (PNG)</button>
            <a href={url} target="_blank" rel="noreferrer" className="text-xs bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-full px-4 py-1.5" data-testid="test-essai-url">👁️ Tester</a>
          </div>
        </div>

        {/* Controls */}
        <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40 space-y-4 no-print" data-testid="qr-controls">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">SOURCE (POUR TES STATISTIQUES)</div>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" data-testid="essai-source-select">
              {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">Chaque source est suivie dans le CRM (tu sais d'où viennent tes inscriptions).</p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">STYLE DE MESSAGE</div>
            <div className="flex flex-col gap-2">
              {PRESETS.map((p) => (
                <button key={p.id} onClick={() => applyPreset(p.id)} data-testid={`preset-${p.id}`}
                  className={`text-left text-xs rounded-lg px-3 py-2 border transition ${presetId === p.id ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">TITRE (AU-DESSUS DU QR)</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" data-testid="essai-title-input" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">POINTS (SOUS LE QR · 1 PAR LIGNE)</div>
            <textarea value={bullets} onChange={(e) => setBullets(e.target.value)} rows={4} className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm font-mono" data-testid="essai-bullets-input" />
          </div>
          <div className="border border-zinc-800 rounded-lg p-4 bg-black/30 text-sm text-zinc-300 leading-relaxed">
            💡 <strong>Astuce</strong> : imprime l'affiche en format A4, plastifie-la et place-la à l'entrée.
            Tu peux aussi télécharger le QR seul pour l'intégrer dans tes posts Instagram/Facebook.
          </div>
        </div>
      </div>

      {/* Affiche IA — porte du dojo */}
      <div className="border border-red-500/40 bg-gradient-to-br from-zinc-950 to-black rounded-2xl p-6 no-print" data-testid="poster-ia-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="text-red-400 text-[10px] tracking-[0.4em] mb-2">🎨 AFFICHE IA · PORTE DU DOJO</div>
            <h2 className="text-lg font-bold">Génère ton affiche professionnelle en 1 clic</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
              L'IA crée une affiche verticale haut de gamme (rouge / noir / blanc) avec ton logo officiel KAIZEN,
              « DEVENIR GLADIATEUR DU QUOTIDIEN », tes disciplines et tes coordonnées — et ton <strong>vrai code QR</strong>
              du cours d'essai est <strong>incrusté automatiquement</strong>. Prête à imprimer pour la porte du dojo.
            </p>
          </div>
          <Button onClick={generatePoster} disabled={posterLoading}
            className="bg-red-600 hover:bg-red-500 text-white font-semibold whitespace-nowrap" data-testid="generate-poster-btn">
            {posterLoading ? "⏳ Génération…" : "🎨 Générer l'affiche IA"}
          </Button>
        </div>

        {/* Choix de format */}
        <div className="mb-4" data-testid="poster-format-selector">
          <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">FORMAT</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "porte", label: "🚪 Affiche porte", sub: "Vertical · impression" },
              { id: "carre", label: "📷 Post Instagram", sub: "Carré 1:1" },
              { id: "story", label: "📱 Story / Reel", sub: "Vertical 9:16" },
            ].map((f) => (
              <button key={f.id} onClick={() => setPosterFormat(f.id)} data-testid={`poster-format-${f.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${posterFormat === f.id ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{f.label}</div>
                <div className="text-[10px] text-zinc-500">{f.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {posterLoading && (
          <GenProgress value={posterProgress} colorClass="bg-red-500" testid="poster-loading" />
        )}

        {posterUrl && !posterLoading && (
          <div className="flex flex-col items-center gap-4" data-testid="poster-result">
            <img src={posterUrl} alt="Affiche dojo générée" className="max-h-[640px] rounded-xl border border-zinc-800 shadow-2xl" data-testid="poster-image" />
            <div className="w-full max-w-xl">
              <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">LÉGENDE (FACULTATIVE · POUR LES RÉSEAUX)</div>
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3}
                placeholder="Laisse vide pour une légende auto (cours d'essai (19,98 $), disciplines, Val-d'Or, hashtags)…"
                className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" data-testid="poster-caption-input" />
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={downloadPoster} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="download-poster-btn">
                📥 Télécharger l'affiche (PNG)
              </Button>
              <Button onClick={publishPoster} disabled={publishing} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="publish-poster-btn">
                {publishing ? "⏳ Publication…" : "📣 Publier sur Facebook & Instagram"}
              </Button>
              <Button onClick={publishLinkedIn} disabled={publishingLi} className="bg-[#0A66C2] hover:bg-[#0959aa] text-white" data-testid="publish-linkedin-btn">
                {publishingLi ? "⏳ Publication…" : "in Publier sur LinkedIn"}
              </Button>
              <Button onClick={generatePoster} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="regenerate-poster-btn">
                🔄 Générer une autre version
              </Button>
            </div>
            <p className="text-[11px] text-zinc-600">Chaque génération produit une variante unique. Le code QR pointe vers ta page d'essai (source : {source}). La publication utilise tes pages connectées dans /app/facebook.</p>
          </div>
        )}
      </div>

      {/* AFFICHE QR · VILLE & ENTREPRISES (à coller partout : compagnies, vitrines…) */}
      <div className="border border-red-500/40 bg-gradient-to-br from-zinc-950 to-black rounded-2xl p-6 no-print" data-testid="qr-ville-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="text-red-400 text-[10px] tracking-[0.4em] mb-2">🎟️ AFFICHE QR · VILLE & ENTREPRISES</div>
            <h2 className="text-lg font-bold">Affiche QR à coller partout en ville</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
              Une affiche centrée sur ton <strong>code QR</strong> du cours d'essai (19,98 $) — à afficher dans les <strong>compagnies, commerces et vitrines</strong>. Les gens scannent et s'inscrivent seuls.
              Choisis le <strong>style</strong> et le <strong>format</strong>, personnalise le texte, puis génère.
            </p>
          </div>
          <Button onClick={generateQrVille} disabled={qvLoading}
            className="bg-red-600 hover:bg-red-500 text-white font-semibold whitespace-nowrap" data-testid="generate-qrville-btn">
            {qvLoading ? "⏳ Génération…" : "🎟️ Générer l'affiche QR"}
          </Button>
          <PosterPackButton
            endpoint="/poster/qr-affiche/pack"
            getBody={() => ({
              style: qvStyle, format: qvFormat, title: qvTitle,
              bullets: qvBullets.split("\n").map((s) => s.trim()).filter(Boolean),
              cta: qvCta, qr_url: url, emplacement: qvEmplacement,
            })}
            filename="pack-affiches-qr.zip"
            label="📦 Pack impression (4 formats · aperçu + ZIP)"
            testId="download-pack-btn"
            disabled={qvLoading}
            className="inline-flex items-center gap-2 rounded-md border border-red-500/50 text-red-300 hover:bg-red-500/10 font-semibold whitespace-nowrap px-4 py-2 text-sm disabled:opacity-50"
          />
        </div>

        {/* Style */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">STYLE</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "epuree", label: "🖨️ Épurée (impression)", sub: "QR géant + slogan · lisible de loin · instantané" },
              { id: "ia", label: "🎨 Visuel IA", sub: "Affiche pro rouge/noir · QR incrusté · ~60 s" },
            ].map((s) => (
              <button key={s.id} onClick={() => setQvStyle(s.id)} data-testid={`qrville-style-${s.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${qvStyle === s.id ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{s.label}</div>
                <div className="text-[10px] text-zinc-500">{s.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Format */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">FORMAT</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "porte", label: "🚪 Porte 120×60 cm", sub: "Vertical · impression" },
              { id: "a4", label: "📄 Mural A3/A4", sub: "Vertical · impression" },
              { id: "carre", label: "📷 Post Instagram", sub: "Carré 1:1" },
              { id: "story", label: "📱 Story / Reel", sub: "Vertical 9:16" },
            ].map((f) => (
              <button key={f.id} onClick={() => setQvFormat(f.id)} data-testid={`qrville-format-${f.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${qvFormat === f.id ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{f.label}</div>
                <div className="text-[10px] text-zinc-500">{f.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Texte éditable */}
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">TITRE</div>
            <input value={qvTitle} onChange={(e) => setQvTitle(e.target.value)} data-testid="qrville-title"
              className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
            <div className="text-[10px] tracking-[0.3em] text-red-400 mt-3 mb-2">RUBAN (BAS)</div>
            <input value={qvCta} onChange={(e) => setQvCta(e.target.value)} data-testid="qrville-cta"
              className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
            <div className="text-[10px] tracking-[0.3em] text-red-400 mt-3 mb-2">📍 EMPLACEMENT (POUR LE SUIVI)</div>
            <input value={qvEmplacement} onChange={(e) => setQvEmplacement(e.target.value)} data-testid="qrville-emplacement"
              placeholder="Ex : Tim Hortons Centre-ville, Gym Éclipse…"
              className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">POINTS (1 PAR LIGNE)</div>
            <textarea value={qvBullets} onChange={(e) => setQvBullets(e.target.value)} rows={4} data-testid="qrville-bullets"
              className="w-full bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
          </div>
        </div>

        {qvLoading && <GenProgress value={qvProgress} colorClass="bg-red-500" testid="qrville-loading" />}

        {qvUrl && !qvLoading && (
          <div className="flex flex-col items-center gap-4" data-testid="qrville-result">
            <img src={qvUrl} alt="Affiche QR ville générée" className="max-h-[640px] rounded-xl border border-zinc-800 shadow-2xl" data-testid="qrville-image" />
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => setQvPreview(true)} variant="outline" className="border-sky-500/50 text-sky-300" data-testid="preview-qrville-btn">
                👁️ Aperçu
              </Button>
              <Button onClick={downloadQrVille} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="download-qrville-btn">
                📥 Télécharger l'affiche (PNG)
              </Button>
              <Button onClick={generateQrVille} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="regenerate-qrville-btn">
                🔄 Générer une autre version
              </Button>
            </div>
            <p className="text-[11px] text-zinc-600">Le QR pointe vers ta page d'essai (source : {source}). Imprime en A4/A3, plastifie et colle en entreprise ou en vitrine.</p>
          </div>
        )}

        {/* Suivi des scans → inscriptions (ROI) */}
        <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="qrville-scans-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-200">📊 Suivi des scans → inscriptions</h3>
            <Button size="sm" variant="outline" onClick={loadQvScans} className="border-zinc-700 text-zinc-300 h-7 text-xs" data-testid="refresh-qvscans-btn">🔄 Actualiser</Button>
          </div>
          {!qvScans ? (
            <p className="text-xs text-zinc-600">Clique « Actualiser » pour voir combien de personnes scannent tes affiches et s'inscrivent.</p>
          ) : qvScans.campaigns.length === 0 ? (
            <p className="text-xs text-zinc-600">Aucune affiche QR traçable encore. Génère-en une pour démarrer le suivi.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-rose-300" data-testid="qv-kpi-scans">{qvScans.totals.scans}</div><div className="text-[10px] text-zinc-500">Scans</div></div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-emerald-400" data-testid="qv-kpi-signups">{qvScans.totals.signups}</div><div className="text-[10px] text-zinc-500">Inscriptions</div></div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-amber-300">{qvScans.totals.conversion_pct}%</div><div className="text-[10px] text-zinc-500">Conversion</div></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-2">Code</th><th className="py-1 pr-2">📍 Emplacement</th><th className="py-1 pr-2">Format</th><th className="py-1 pr-2 text-center">Scans</th><th className="py-1 pr-2 text-center">Inscr.</th><th className="py-1 text-center">Conv.</th></tr></thead>
                  <tbody>
                    {qvScans.campaigns.map((c) => (
                      <tr key={c.code} className="border-t border-zinc-900" data-testid={`qv-row-${c.code}`}>
                        <td className="py-1 pr-2 font-mono text-rose-300">{c.code}</td>
                        <td className="py-1 pr-2 text-zinc-300">{c.emplacement || "—"}</td>
                        <td className="py-1 pr-2 text-zinc-400">{c.format}</td>
                        <td className="py-1 pr-2 text-center text-zinc-300">{c.scans}</td>
                        <td className="py-1 pr-2 text-center text-emerald-400">{c.signups}</td>
                        <td className="py-1 text-center text-amber-300">{c.conversion_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-zinc-600">Chaque affiche a un code unique dans son QR. Quand quelqu'un scanne, le scan est compté ; s'il s'inscrit, l'inscription est reliée à ce code.</p>
            </div>
          )}
        </div>

        {/* Aperçu de l'affiche QR */}
        {qvPreview && qvUrl && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="qrville-preview-modal" onClick={() => setQvPreview(false)}>
            <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
                {(qvFormat === "carre" || qvFormat === "story") ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-zinc-200">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-red-500 to-amber-400 flex items-center justify-center text-white text-xs font-bold">K</div>
                      <div className="text-sm font-semibold text-zinc-900">institut_impact_elite</div>
                      <span className="ml-auto text-zinc-400">•••</span>
                    </div>
                    <img src={qvUrl} alt="Aperçu affiche" className="w-full object-contain bg-zinc-100" data-testid="qrville-preview-image" />
                    <div className="px-3 py-2 text-zinc-800 text-xl">❤️ 💬 ✈️</div>
                  </>
                ) : (
                  <img src={qvUrl} alt="Aperçu affiche impression" className="w-full object-contain bg-zinc-100" data-testid="qrville-preview-image" />
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setQvPreview(false)} className="flex-1 py-2 rounded-lg bg-zinc-800 text-zinc-200 text-sm font-semibold" data-testid="qrville-preview-close">Fermer</button>
                <button onClick={() => { setQvPreview(false); downloadQrVille(); }} className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">📥 Télécharger</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Affiche IA — PRESTIGE & DISCIPLINES (2ᵉ porte 120×60 cm) */}
      <div className="border border-amber-500/40 bg-gradient-to-br from-zinc-950 to-black rounded-2xl p-6 no-print" data-testid="prestige-poster-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">🏆 AFFICHE PRESTIGE · 2ᵉ PORTE</div>
            <h2 className="text-lg font-bold">Affiche de prestige & disciplines (120×60 cm)</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
              Affiche premium sombre/rouge/<strong>doré</strong> pour ta 2ᵉ porte : liste de <strong>toutes les disciplines</strong>,
              copywriting d'élite (ceintures noires & champions formés, expertise internationale, <strong>entraînement en ligne 24/7 · 365</strong>),
              co-branding « Institut Impact Élite Martial × Kaizen System ». Le <strong>QR du cours d'essai</strong> est incrusté automatiquement.
            </p>
          </div>
          <Button onClick={generatePrestige} disabled={prestigeLoading}
            className="bg-amber-600 hover:bg-amber-500 text-white font-semibold whitespace-nowrap" data-testid="generate-prestige-btn">
            {prestigeLoading ? "⏳ Génération…" : "🏆 Générer l'affiche Prestige"}
          </Button>
          <PosterPackButton
            endpoint="/poster/prestige/pack"
            getBody={() => ({ style: prestigeStyle, emplacement: prestigeEmpl })}
            filename="pack-affiches-prestige.zip"
            label="📦 Pack 4 formats (aperçu + ZIP)"
            testId="prestige-pack-btn"
            disabled={prestigeLoading}
            className="inline-flex items-center gap-2 rounded-md border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 font-semibold whitespace-nowrap px-4 py-2 text-sm disabled:opacity-50"
          />
        </div>

        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">STYLE</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "epuree", label: "🖨️ Épurée (impression)", sub: "QR géant + slogan · instantané" },
              { id: "ia", label: "🎨 Visuel IA", sub: "Affiche pro · disciplines · ~60 s" },
            ].map((s) => (
              <button key={s.id} onClick={() => setPrestigeStyle(s.id)} data-testid={`prestige-style-${s.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${prestigeStyle === s.id ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{s.label}</div>
                <div className="text-[10px] text-zinc-500">{s.sub}</div>
              </button>
            ))}
          </div>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mt-3 mb-2">📍 EMPLACEMENT (POUR LE SUIVI)</div>
          <input value={prestigeEmpl} onChange={(e) => setPrestigeEmpl(e.target.value)} data-testid="prestige-emplacement"
            placeholder="Ex : 2ᵉ porte du dojo, vitrine partenaire…"
            className="w-full max-w-md bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
        </div>

        <div className="mb-4" data-testid="prestige-format-selector">
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">FORMAT</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "porte", label: "🚪 Porte 120×60 cm", sub: "Vertical · impression" },
              { id: "a4", label: "📄 Mural A3/A4", sub: "Vertical · impression" },
              { id: "carre", label: "📷 Post Instagram", sub: "Carré 1:1" },
              { id: "story", label: "📱 Story / Reel", sub: "Vertical 9:16" },
            ].map((f) => (
              <button key={f.id} onClick={() => setPrestigeFormat(f.id)} data-testid={`prestige-format-${f.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${prestigeFormat === f.id ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{f.label}</div>
                <div className="text-[10px] text-zinc-500">{f.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {prestigeLoading && (
          <GenProgress value={prestigeProgress} colorClass="bg-amber-500" testid="prestige-loading" />
        )}

        {prestigeUrl && !prestigeLoading && (
          <div className="flex flex-col items-center gap-4" data-testid="prestige-result">
            <img src={prestigeUrl} alt="Affiche prestige générée" className="max-h-[640px] rounded-xl border border-zinc-800 shadow-2xl" data-testid="prestige-image" />
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={downloadPrestige} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="download-prestige-btn">
                📥 Télécharger l'affiche (PNG)
              </Button>
              <Button onClick={generatePrestige} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="regenerate-prestige-btn">
                🔄 Générer une autre version
              </Button>
            </div>
            <p className="text-[11px] text-zinc-600">Le code QR pointe vers ta page du cours d'essai. Imprime en 120×60 cm pour la 2ᵉ porte.</p>
          </div>
        )}
      </div>

      {/* Affiche IA — POWERKICK HER (empowerment féminin · QR inscription) */}
      <div className="border border-rose-500/40 bg-gradient-to-br from-zinc-950 to-black rounded-2xl p-6 no-print" data-testid="powerkick-poster-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="text-rose-400 text-[10px] tracking-[0.4em] mb-2">🥊 AFFICHE POWERKICK HER</div>
            <h2 className="text-lg font-bold">Affiche PowerKick Her (empowerment féminin)</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
              Affiche premium <strong className="text-rose-300">rose / noir / doré</strong> pour le programme 100% femmes :
              puissance, confiance, communauté sans jugement, et appel à l'action « <strong>cours d'essai — 19,98 $</strong> ».
              Le <strong>QR d'inscription</strong> (page PowerKick Her) est incrusté automatiquement — à afficher toute l'année pour attirer les passantes.
            </p>
          </div>
          <Button onClick={generatePowerkick} disabled={pkhLoading}
            className="bg-rose-600 hover:bg-rose-500 text-white font-semibold whitespace-nowrap" data-testid="generate-powerkick-btn">
            {pkhLoading ? "⏳ Génération…" : "🥊 Générer l'affiche PowerKick Her"}
          </Button>
        </div>

        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] text-rose-400 mb-2">STYLE</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "epuree", label: "🖨️ Épurée (impression)", sub: "QR géant + slogan · instantané" },
              { id: "ia", label: "🎨 Visuel IA", sub: "Affiche rose/noir/doré · ~60 s" },
            ].map((s) => (
              <button key={s.id} onClick={() => setPkhStyle(s.id)} data-testid={`powerkick-style-${s.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${pkhStyle === s.id ? "bg-rose-500/20 border-rose-500/50 text-rose-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{s.label}</div>
                <div className="text-[10px] text-zinc-500">{s.sub}</div>
              </button>
            ))}
          </div>
          <div className="text-[10px] tracking-[0.3em] text-rose-400 mt-3 mb-2">📍 EMPLACEMENT (POUR LE SUIVI)</div>
          <input value={pkhEmpl} onChange={(e) => setPkhEmpl(e.target.value)} data-testid="powerkick-emplacement"
            placeholder="Ex : Gym Éclipse, salon de coiffure…"
            className="w-full max-w-md bg-black border border-zinc-800 rounded-lg p-2.5 text-sm" />
        </div>

        <div className="mb-4" data-testid="powerkick-format-selector">
          <div className="text-[10px] tracking-[0.3em] text-rose-400 mb-2">FORMAT</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "porte", label: "🚪 Porte 120×60 cm", sub: "Vertical · impression" },
              { id: "a4", label: "📄 Mural A3/A4", sub: "Vertical · impression" },
              { id: "carre", label: "📷 Post Instagram", sub: "Carré 1:1" },
              { id: "story", label: "📱 Story / Reel", sub: "Vertical 9:16" },
            ].map((f) => (
              <button key={f.id} onClick={() => setPkhFormat(f.id)} data-testid={`powerkick-format-${f.id}`}
                className={`text-left rounded-lg px-4 py-2 border transition ${pkhFormat === f.id ? "bg-rose-500/20 border-rose-500/50 text-rose-200" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <div className="text-xs font-semibold">{f.label}</div>
                <div className="text-[10px] text-zinc-500">{f.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {pkhLoading && (
          <GenProgress value={pkhProgress} colorClass="bg-rose-500" testid="powerkick-loading" />
        )}

        {pkhUrl && !pkhLoading && (
          <div className="flex flex-col items-center gap-4" data-testid="powerkick-result">
            <img src={pkhUrl} alt="Affiche PowerKick Her générée" className="max-h-[640px] rounded-xl border border-zinc-800 shadow-2xl" data-testid="powerkick-image" />
            {pkhCode && (
              <div className="text-center bg-rose-500/10 border border-rose-500/40 rounded-lg px-4 py-2" data-testid="powerkick-code">
                <span className="text-zinc-400 text-xs">Code promo unique de cette affiche : </span>
                <span className="font-mono font-bold text-rose-300">{pkhCode}</span>
                <p className="text-[11px] text-zinc-500 mt-1">Le QR compte les scans et chaque inscription liée à ce code (mesure du ROI ci-dessous).</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={downloadPowerkick} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="download-powerkick-btn">
                📥 Télécharger l'affiche (PNG)
              </Button>
              <Button onClick={generatePowerkick} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="regenerate-powerkick-btn">
                🔄 Générer une autre version
              </Button>
            </div>
            <p className="text-[11px] text-zinc-600">Le code QR pointe vers l'inscription PowerKick Her et débloque l'offre « 1ᵉʳ mois -50% ».</p>
          </div>
        )}

        {/* Tableau de bord ROI des affiches */}
        <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="powerkick-campaigns">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-200">📊 ROI des affiches (scans → inscriptions)</h3>
            <Button size="sm" variant="outline" onClick={loadCampaigns} className="border-zinc-700 text-zinc-300 h-7 text-xs" data-testid="refresh-campaigns-btn">
              🔄 Actualiser
            </Button>
          </div>
          {!pkhCampaigns ? (
            <p className="text-xs text-zinc-600">Clique « Actualiser » pour charger les statistiques.</p>
          ) : pkhCampaigns.campaigns.length === 0 ? (
            <p className="text-xs text-zinc-600">Aucune affiche traçable encore. Génère-en une pour démarrer le suivi.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-rose-300" data-testid="kpi-scans">{pkhCampaigns.totals.scans}</div><div className="text-[10px] text-zinc-500">Scans</div></div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-emerald-400" data-testid="kpi-signups">{pkhCampaigns.totals.signups}</div><div className="text-[10px] text-zinc-500">Inscriptions</div></div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg py-2"><div className="text-lg font-bold text-amber-300" data-testid="kpi-conv">{pkhCampaigns.totals.conversion_pct}%</div><div className="text-[10px] text-zinc-500">Conversion</div></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-2">Code</th><th className="py-1 pr-2">Format</th><th className="py-1 pr-2 text-center">Scans</th><th className="py-1 pr-2 text-center">Inscr.</th><th className="py-1 text-center">Conv.</th></tr></thead>
                  <tbody>
                    {pkhCampaigns.campaigns.map((c) => (
                      <tr key={c.code} className="border-t border-zinc-900">
                        <td className="py-1 pr-2 font-mono text-rose-300">{c.code}</td>
                        <td className="py-1 pr-2 text-zinc-400">{c.format}</td>
                        <td className="py-1 pr-2 text-center text-zinc-300">{c.scans}</td>
                        <td className="py-1 pr-2 text-center text-emerald-400">{c.signups}</td>
                        <td className="py-1 text-center text-amber-300">{c.conversion_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Affiche imprimable */}
      <div className="print-essai hidden">
        <div className="pe-wrap">
          <div className="pe-kicker">INSTITUT IMPACT ÉLITE MARTIAL · VAL-D'OR</div>
          <div className="pe-title">{title}</div>
          <div className="pe-qr">
            <QRCodeSVG value={url} size={420} bgColor="#FFFFFF" fgColor="#000000" level="M" />
          </div>
          <ul className="pe-bullets">
            {bulletList.map((b, i) => <li key={i}>✓ {b}</li>)}
          </ul>
          <div className="pe-steps">Pointe la caméra de ton téléphone sur le code · Remplis le formulaire · On te contacte !</div>
          <div className="pe-foot">Muay Thaï · Kick-Boxing · Jiu-Jitsu Brésilien — Présenté par KAIZEN System</div>
        </div>
      </div>

      <style>{`
        .print-essai { display: none; }
        @media print {
          body * { visibility: hidden !important; }
          .print-essai, .print-essai * { visibility: visible !important; }
          .print-essai {
            display: block !important;
            position: absolute !important; left: 0; top: 0;
            width: 210mm; min-height: 297mm; background: white !important; color: #0E0E0E !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          }
          .pe-wrap { text-align: center; padding: 25mm 15mm; }
          .pe-kicker { font-size: 11pt; letter-spacing: 5px; color: #C8102E; font-weight: 700; }
          .pe-title { font-size: 34pt; font-weight: 900; margin: 12mm 0 8mm; line-height: 1.1; }
          .pe-qr { display: flex; justify-content: center; margin-bottom: 10mm; }
          .pe-bullets { list-style: none; padding: 0; margin: 0 auto 12mm; max-width: 150mm; text-align: left; display: inline-block; }
          .pe-bullets li { font-size: 15pt; color: #1a1a1a; margin: 3mm 0; font-weight: 600; }
          .pe-steps { font-size: 12pt; color: #444; max-width: 150mm; margin: 0 auto 18mm; line-height: 1.6; }
          .pe-foot { font-size: 9pt; color: #888; letter-spacing: 1px; border-top: 2px solid #C8102E; padding-top: 6mm; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
